import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Building2, ShieldCheck, UserPlus } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { canManageUsers, normalizeRole } from "../lib/permissions";
import { logActivity } from "../lib/auditLog";

function SectionCard({ title, description, children, isDark }) {
  return (
    <section
      className={`rounded-3xl border p-6 shadow-sm ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="mb-5">
        <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
          {title}
        </h3>
        <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          {description}
        </p>
      </div>
      {children}
    </section>
  );
}

function Field({ label, hint, children, isDark }) {
  return (
    <div className="space-y-2">
      <div className="min-h-[2.75rem]">
        <p className={`text-sm font-medium ${isDark ? "text-slate-200" : "text-slate-800"}`}>
          {label}
        </p>
        {hint ? (
          <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>{hint}</p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, isDark, type = "text", ...props }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      {...props}
      className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${
        isDark
          ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
          : "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
      }`}
    />
  );
}

function Select({ value, onChange, options, isDark }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${
        isDark
          ? "border-slate-700 bg-slate-950 text-white"
          : "border-slate-200 bg-white text-slate-900"
      }`}
    >
      {options.map((option) => (
        <option key={`${option.value}-${option.label}`} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

const orgBootstrapDefaults = {
  organizationName: "",
  organizationSlug: "",
  billingEmail: "",
  administratorFullName: "",
  administratorEmail: "",
  administratorPassword: "",
  administratorStatus: "Active",
};

const newUserDefaults = {
  organizationId: "",
  fullName: "",
  email: "",
  password: "",
  role: "Manager",
  status: "Active",
  tenantId: "",
};

const businessToTechnicalRole = {
  Growth: "growth",
  Administrator: "admin",
  Manager: "manager",
  Accountant: "accountant",
  Maintenance: "maintenance",
  Viewer: "viewer",
  Tenant: "tenant",
};

async function getFunctionErrorDescription(error, fallbackMessage) {
  if (!error) {
    return fallbackMessage;
  }

  const directMessage = String(error.message || "").trim();

  if (directMessage && !directMessage.includes("non-2xx status code")) {
    return directMessage;
  }

  try {
    const response = error.context;
    if (!response || typeof response.clone !== "function") {
      return directMessage || fallbackMessage;
    }

    const payload = await response.clone().json();
    const payloadMessage = String(payload?.error || payload?.message || "").trim();
    return payloadMessage || directMessage || fallbackMessage;
  } catch (_contextError) {
    return directMessage || fallbackMessage;
  }
}

function extractOrganizationId(profile, user, sessionClaims) {
  return (
    profile?.organization_id ||
    user?.app_metadata?.organization_id ||
    user?.app_metadata?.organizationId ||
    user?.user_metadata?.organization_id ||
    user?.user_metadata?.organizationId ||
    sessionClaims?.app_metadata?.organization_id ||
    sessionClaims?.app_metadata?.organizationId ||
    sessionClaims?.user_metadata?.organization_id ||
    sessionClaims?.user_metadata?.organizationId ||
    ""
  );
}

function createSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export default function UserManagementPage({ isDark, setIsDark, currency, setCurrency }) {
  const toast = useToast();
  const { user, profile, refreshProfile, sessionClaims } = useAuth();
  const normalizedRole = normalizeRole(profile?.role);
  const currentOrganizationId = extractOrganizationId(profile, user, sessionClaims);
  const isGrowthUser = normalizedRole === "Growth";
  const userCanManageUsers = canManageUsers(profile?.role);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [accessMessage, setAccessMessage] = useState("");
  const [accessError, setAccessError] = useState("");
  const [bootstrapMessage, setBootstrapMessage] = useState("");
  const [bootstrapError, setBootstrapError] = useState("");
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [provisioningLoading, setProvisioningLoading] = useState(false);
  const [orgBootstrap, setOrgBootstrap] = useState(orgBootstrapDefaults);
  const [newUser, setNewUser] = useState({
    ...newUserDefaults,
    organizationId: currentOrganizationId || "",
  });

  const activeOrganizationId = isGrowthUser
    ? newUser.organizationId || organizations[0]?.id || ""
    : currentOrganizationId || "";

  const userRoleOptions = useMemo(
    () => [
      ...(isGrowthUser ? [{ value: "Growth", label: "Growth" }] : []),
      { value: "Administrator", label: "Administrator" },
      { value: "Manager", label: "Manager" },
      { value: "Accountant", label: "Accountant" },
      { value: "Maintenance", label: "Maintenance" },
      { value: "Viewer", label: "Viewer" },
      { value: "Tenant", label: "Tenant" },
    ],
    [isGrowthUser]
  );

  const activeUserRoleOptions = userRoleOptions;

  const updateNewUser = (key, value) => {
    setNewUser((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "role" && value !== "Tenant" ? { tenantId: "" } : {}),
      ...(key === "organizationId" ? { tenantId: "" } : {}),
    }));
  };

  const updateOrgBootstrap = (key, value) => {
    setOrgBootstrap((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "organizationName" && !prev.organizationSlug
        ? { organizationSlug: createSlug(value) }
        : {}),
    }));
  };

  const fetchUsers = useCallback(async () => {
    if (!userCanManageUsers || !supabase) {
      setUsers([]);
      return;
    }

    if (isGrowthUser && !activeOrganizationId) {
      setUsers([]);
      return;
    }

    setUsersLoading(true);

    let query = supabase
      .from("user_profiles")
      .select("*")
      .order("created_at", { ascending: false });

    if (activeOrganizationId) {
      query = query.eq("organization_id", activeOrganizationId);
    }

    const { data, error } = await query;
    setUsers(!error ? data || [] : []);
    setUsersLoading(false);
  }, [activeOrganizationId, isGrowthUser, userCanManageUsers]);

  const fetchOrganizations = useCallback(async () => {
    if (!userCanManageUsers || !supabase) {
      setOrganizations([]);
      return;
    }

    if (!isGrowthUser) {
      if (currentOrganizationId) {
        setOrganizations([
          {
            id: currentOrganizationId,
            name: profile?.organization_name || "Current organization",
            slug: profile?.organization_slug || "current-organization",
            status: "active",
          },
        ]);
      } else {
        setOrganizations([]);
      }
      return;
    }

    setOrganizationsLoading(true);
    const { data, error } = await supabase
      .from("organizations")
      .select("id, name, slug, status")
      .order("name", { ascending: true });
    setOrganizations(!error ? data || [] : []);
    setOrganizationsLoading(false);
  }, [currentOrganizationId, isGrowthUser, profile?.organization_name, profile?.organization_slug, userCanManageUsers]);

  const fetchTenants = useCallback(async () => {
    if (!userCanManageUsers || !supabase || !activeOrganizationId || newUser.role !== "Tenant") {
      setTenants([]);
      return;
    }

    setTenantsLoading(true);
    const { data, error } = await supabase
      .from("tenants")
      .select("id, full_name, email, unit, property_name, organization_id")
      .eq("organization_id", activeOrganizationId)
      .order("full_name", { ascending: true });
    setTenants(!error ? data || [] : []);
    setTenantsLoading(false);
  }, [activeOrganizationId, newUser.role, userCanManageUsers]);

  const fetchActivityLogs = useCallback(async () => {
    if (!userCanManageUsers || !supabase) {
      setActivityLogs([]);
      return;
    }

    setActivityLoading(true);
    const { data, error } = await supabase
      .from("activity_logs")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(12);
    if (!error) {
      setActivityLogs(data || []);
    }
    setActivityLoading(false);
  }, [userCanManageUsers]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    fetchActivityLogs();
  }, [fetchActivityLogs]);

  useEffect(() => {
    setNewUser((prev) => ({
      ...prev,
      organizationId: prev.organizationId || currentOrganizationId || "",
    }));
  }, [currentOrganizationId]);

  const handleCreateOrganization = async (event) => {
    event.preventDefault();
    setBootstrapMessage("");
    setBootstrapError("");

    if (!isGrowthUser || !supabase) {
      setBootstrapError("Only Growth users can create organizations.");
      return;
    }

    setBootstrapLoading(true);
    const { data, error } = await supabase.functions.invoke("create-organization-bootstrap", {
      body: {
        organization: {
          name: orgBootstrap.organizationName,
          slug: orgBootstrap.organizationSlug || createSlug(orgBootstrap.organizationName),
          billingEmail: orgBootstrap.billingEmail || null,
        },
        administrator: {
          fullName: orgBootstrap.administratorFullName,
          email: orgBootstrap.administratorEmail,
          temporaryPassword: orgBootstrap.administratorPassword,
          status: orgBootstrap.administratorStatus,
        },
      },
    });
    setBootstrapLoading(false);

    if (error) {
      const description = await getFunctionErrorDescription(
        error,
        "Please review the organization details and try again."
      );
      setBootstrapError(description);
      toast.error("Organization bootstrap failed", description);
      return;
    }

    const createdOrganizationId = data?.organizationId || "";
    setBootstrapMessage(
      `Created ${data?.organizationName || orgBootstrap.organizationName} and provisioned the first administrator.`
    );
    setOrgBootstrap(orgBootstrapDefaults);
    await fetchOrganizations();
    if (createdOrganizationId) {
      setNewUser((prev) => ({ ...prev, organizationId: createdOrganizationId }));
    }
    toast.success("Organization created", "The first administrator login is now provisioned.");
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setAccessMessage("");
    setAccessError("");

    if (!userCanManageUsers || !supabase) {
      setAccessError("Only Growth or Administrator users can create user access.");
      return;
    }

    if (!activeOrganizationId) {
      setAccessError("Select an organization before creating a user.");
      return;
    }

    const technicalRole = businessToTechnicalRole[newUser.role] || "viewer";

    if (technicalRole === "tenant" && !newUser.tenantId) {
      setAccessError("Select the linked tenant record before creating a tenant login.");
      return;
    }

    if (String(newUser.password || "").trim().length < 8) {
      setAccessError("Temporary password must be at least 8 characters.");
      return;
    }

    setProvisioningLoading(true);
    const { data, error } = await supabase.functions.invoke("create-organization-user", {
      body: {
        organizationId: activeOrganizationId,
        user: {
          fullName: newUser.fullName,
          email: newUser.email,
          temporaryPassword: newUser.password,
          role: technicalRole,
          status: newUser.status,
          tenantId: technicalRole === "tenant" ? newUser.tenantId : null,
        },
      },
    });
    setProvisioningLoading(false);

    if (error) {
      const description = await getFunctionErrorDescription(
        error,
        "Please review the user details and try again."
      );
      setAccessError(description);
      toast.error("User provisioning failed", description);
      return;
    }

    setAccessMessage(`Created ${newUser.role} access for ${data?.email || newUser.email}.`);
    setNewUser({ ...newUserDefaults, organizationId: activeOrganizationId });
    await fetchUsers();
    await fetchActivityLogs();
    await fetchTenants();
    toast.success("User access created", `${data?.email || newUser.email} can now sign in to SpaceM8.`);
  };

  const handleUpdateUserAccess = async (id, changes) => {
    if (!userCanManageUsers || !supabase) {
      setAccessError("Only Growth or Administrator users can update user access.");
      return;
    }

    if (!isGrowthUser && changes.role === "Growth") {
      setAccessError("Only Growth users can assign the Growth role.");
      return;
    }

    const { error } = await supabase
      .from("user_profiles")
      .update({ ...changes, updated_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      setAccessError(error.message || "Unable to update user access.");
      return;
    }

    setAccessMessage("User access updated.");
    await logActivity({
      action: "Updated user access",
      entityType: "user_profile",
      entityId: id,
      entityLabel: id,
      details: changes,
    });
    await fetchUsers();
    if (id === user?.id) {
      await refreshProfile();
    }
  };

  const activeOrganizationLabel = organizations.find((organization) => organization.id === activeOrganizationId)?.name
    || profile?.organization_name
    || "Current organization";

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Access Control"
      title="User Management"
      description="Create organizations, provision organization-scoped logins, and review access activity."
    >
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_24rem]">
        <div className="space-y-6">
          <SectionCard
            title="Organization Bootstrap"
            description="Create a new organization and its first administrator from a trusted Growth session."
            isDark={isDark}
          >
            {isGrowthUser ? (
              <form onSubmit={handleCreateOrganization} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Organization name" isDark={isDark}>
                    <Input
                      value={orgBootstrap.organizationName}
                      onChange={(event) => updateOrgBootstrap("organizationName", event.target.value)}
                      placeholder="Acme Property Management"
                      isDark={isDark}
                      required
                    />
                  </Field>

                  <Field
                    label="Organization slug"
                    hint="Used as the unique organization handle."
                    isDark={isDark}
                  >
                    <Input
                      value={orgBootstrap.organizationSlug}
                      onChange={(event) => updateOrgBootstrap("organizationSlug", event.target.value)}
                      placeholder="acme-property-management"
                      isDark={isDark}
                      required
                    />
                  </Field>

                  <Field label="Billing email" isDark={isDark}>
                    <Input
                      value={orgBootstrap.billingEmail}
                      onChange={(event) => updateOrgBootstrap("billingEmail", event.target.value)}
                      placeholder="ops@acme.example"
                      isDark={isDark}
                      type="email"
                    />
                  </Field>

                  <Field label="First administrator full name" isDark={isDark}>
                    <Input
                      value={orgBootstrap.administratorFullName}
                      onChange={(event) => updateOrgBootstrap("administratorFullName", event.target.value)}
                      placeholder="Jane Admin"
                      isDark={isDark}
                      required
                    />
                  </Field>

                  <Field label="First administrator email" isDark={isDark}>
                    <Input
                      value={orgBootstrap.administratorEmail}
                      onChange={(event) => updateOrgBootstrap("administratorEmail", event.target.value)}
                      placeholder="jane.admin@acme.example"
                      isDark={isDark}
                      type="email"
                      required
                    />
                  </Field>

                  <Field label="Temporary password" isDark={isDark}>
                    <Input
                      value={orgBootstrap.administratorPassword}
                      onChange={(event) => updateOrgBootstrap("administratorPassword", event.target.value)}
                      placeholder="At least 8 characters"
                      isDark={isDark}
                      type="password"
                      required
                    />
                  </Field>
                </div>

                {bootstrapError ? (
                  <div
                    className={`rounded-2xl border px-4 py-3 text-sm ${
                      isDark
                        ? "border-rose-800 bg-rose-950 text-rose-300"
                        : "border-rose-200 bg-rose-50 text-rose-700"
                    }`}
                  >
                    {bootstrapError}
                  </div>
                ) : null}

                {bootstrapMessage ? (
                  <div
                    className={`rounded-2xl border px-4 py-3 text-sm ${
                      isDark
                        ? "border-emerald-800 bg-emerald-950 text-emerald-300"
                        : "border-emerald-200 bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    {bootstrapMessage}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={bootstrapLoading}
                  className={`inline-flex h-12 w-full items-center justify-center rounded-2xl bg-slate-900 px-5 font-medium text-white transition ${
                    bootstrapLoading ? "opacity-70" : "hover:bg-slate-800"
                  }`}
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  {bootstrapLoading ? "Creating organization..." : "Create organization and first admin"}
                </button>
              </form>
            ) : (
              <div
                className={`rounded-2xl border p-4 text-sm ${
                  isDark
                    ? "border-slate-800 bg-slate-950 text-slate-300"
                    : "border-slate-200 bg-slate-50 text-slate-600"
                }`}
              >
                Only Growth users can bootstrap a new organization and first administrator.
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Users & Login Access"
            description="Provision organization-scoped logins for staff and tenants."
            isDark={isDark}
          >
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div
                className={`rounded-2xl border px-4 py-3 text-sm ${
                  isDark
                    ? "border-sky-800 bg-sky-950 text-sky-200"
                    : "border-sky-200 bg-sky-50 text-sky-800"
                }`}
              >
                {isGrowthUser
                  ? "Growth users can provision logins for any organization. Choose the target organization first."
                  : "Administrator provisioning is locked to your current organization."}
              </div>

              <Field
                label="Organization"
                hint={
                  isGrowthUser
                    ? "Choose which organization will own this user account."
                    : "This account will be created inside your organization."
                }
                isDark={isDark}
              >
                <Select
                  value={activeOrganizationId}
                  onChange={(event) => updateNewUser("organizationId", event.target.value)}
                  isDark={isDark}
                  options={
                    organizations.length > 0
                      ? organizations.map((organization) => ({
                          value: organization.id,
                          label: `${organization.name}${organization.status ? ` (${organization.status})` : ""}`,
                        }))
                      : [
                          {
                            value: activeOrganizationId || "",
                            label: organizationsLoading
                              ? "Loading organizations..."
                              : activeOrganizationId
                              ? "Current organization"
                              : "No organization available",
                          },
                        ]
                  }
                />
              </Field>

              <Field label="Full name" isDark={isDark}>
                <Input
                  value={newUser.fullName}
                  onChange={(event) => updateNewUser("fullName", event.target.value)}
                  placeholder="Jane Manager"
                  isDark={isDark}
                  required
                />
              </Field>

              <Field label="Email address" isDark={isDark}>
                <Input
                  value={newUser.email}
                  onChange={(event) => updateNewUser("email", event.target.value)}
                  placeholder="jane@example.com"
                  isDark={isDark}
                  type="email"
                  required
                />
              </Field>

              <Field label="Temporary password" hint="User can change this later from Supabase Auth." isDark={isDark}>
                <Input
                  value={newUser.password}
                  onChange={(event) => updateNewUser("password", event.target.value)}
                  placeholder="Minimum 8 characters"
                  isDark={isDark}
                  type="password"
                  required
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Role" isDark={isDark}>
                  <Select
                    value={newUser.role}
                    onChange={(event) => updateNewUser("role", event.target.value)}
                    isDark={isDark}
                    options={userRoleOptions}
                  />
                </Field>

                <Field label="Status" isDark={isDark}>
                  <Select
                    value={newUser.status}
                    onChange={(event) => updateNewUser("status", event.target.value)}
                    isDark={isDark}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Suspended", label: "Suspended" },
                    ]}
                  />
                </Field>
              </div>

              {newUser.role === "Tenant" ? (
                <Field
                  label="Linked tenant record"
                  hint="Tenant logins must be linked to an existing tenant row in the same organization."
                  isDark={isDark}
                >
                  <Select
                    value={newUser.tenantId}
                    onChange={(event) => updateNewUser("tenantId", event.target.value)}
                    isDark={isDark}
                    options={
                      tenants.length > 0
                        ? [
                            { value: "", label: "Select tenant record" },
                            ...tenants.map((tenant) => ({
                              value: tenant.id,
                              label: `${tenant.full_name || tenant.email || tenant.id}${tenant.property_name ? ` - ${tenant.property_name}` : ""}${tenant.unit ? ` / ${tenant.unit}` : ""}`,
                            })),
                          ]
                        : [
                            {
                              value: "",
                              label: tenantsLoading
                                ? "Loading tenant records..."
                                : activeOrganizationId
                                ? "No tenant records found for this organization"
                                : "Select an organization first",
                            },
                          ]
                    }
                  />
                </Field>
              ) : null}

              {accessError ? (
                <div
                  className={`rounded-2xl border px-4 py-3 text-sm ${
                    isDark
                      ? "border-rose-800 bg-rose-950 text-rose-300"
                      : "border-rose-200 bg-rose-50 text-rose-700"
                  }`}
                >
                  {accessError}
                </div>
              ) : null}

              {accessMessage ? (
                <div
                  className={`rounded-2xl border px-4 py-3 text-sm ${
                    isDark
                      ? "border-emerald-800 bg-emerald-950 text-emerald-300"
                      : "border-emerald-200 bg-emerald-50 text-emerald-700"
                  }`}
                >
                  {accessMessage}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={provisioningLoading}
                className={`inline-flex h-12 w-full items-center justify-center rounded-2xl bg-orange-500 px-5 font-medium text-white transition ${
                  provisioningLoading ? "opacity-70" : "hover:bg-orange-600"
                }`}
              >
                <UserPlus className="mr-2 h-4 w-4" />
                {provisioningLoading ? "Creating user access..." : "Create user access"}
              </button>
            </form>

            <div className="mt-6 space-y-3">
              <div className="flex items-center justify-between">
                <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                  Active users
                </p>
                <button
                  type="button"
                  onClick={fetchUsers}
                  className={`text-xs font-semibold text-sky-700 ${usersLoading ? "opacity-60" : ""}`}
                >
                  {usersLoading ? "Refreshing..." : "Refresh"}
                </button>
              </div>

              <div
                className={`rounded-2xl border px-4 py-3 text-xs ${
                  isDark
                    ? "border-amber-800 bg-amber-950 text-amber-300"
                    : "border-amber-200 bg-amber-50 text-amber-700"
                }`}
              >
                Live role and status edits still update the profile row directly. The secure org-aware access update function is the next backend step.
              </div>

              {users.length === 0 ? (
                <div
                  className={`rounded-2xl border p-4 text-sm ${
                    isDark
                      ? "border-slate-800 bg-slate-950 text-slate-400"
                      : "border-slate-200 bg-slate-50 text-slate-500"
                  }`}
                >
                  No user profiles found yet.
                </div>
              ) : (
                users.map((appUser) => (
                  <div
                    key={appUser.id}
                    className={`rounded-2xl border p-4 ${
                      isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div className="mb-3">
                      <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                        {appUser.full_name || appUser.email}
                      </p>
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        {appUser.email}
                      </p>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <Select
                        value={appUser.role || "Manager"}
                        onChange={(event) => handleUpdateUserAccess(appUser.id, { role: event.target.value })}
                        isDark={isDark}
                        options={activeUserRoleOptions}
                      />

                      <Select
                        value={appUser.status || "Active"}
                        onChange={(event) => handleUpdateUserAccess(appUser.id, { status: event.target.value })}
                        isDark={isDark}
                        options={[
                          { value: "Active", label: "Active" },
                          { value: "Suspended", label: "Suspended" },
                        ]}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </SectionCard>
        </div>

        <div className="space-y-6">
          <SectionCard
            title="Access context"
            description="The organization and role boundaries currently active for this management session."
            isDark={isDark}
          >
            <div className="space-y-4">
              <div
                className={`flex items-center gap-3 rounded-2xl border p-4 ${
                  isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                }`}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>Current role</p>
                  <p className={`text-sm font-medium ${isDark ? "text-white" : "text-slate-900"}`}>
                    {profile?.role || "Viewer"}
                  </p>
                </div>
              </div>

              <div
                className={`flex items-center gap-3 rounded-2xl border p-4 ${
                  isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                }`}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>Active organization</p>
                  <p className={`truncate text-sm font-medium ${isDark ? "text-white" : "text-slate-900"}`}>
                    {activeOrganizationLabel}
                  </p>
                </div>
              </div>

              <div
                className={`rounded-2xl border px-4 py-3 text-sm ${
                  isDark
                    ? "border-sky-800 bg-sky-950 text-sky-200"
                    : "border-sky-200 bg-sky-50 text-sky-800"
                }`}
              >
                Growth can switch target organizations from the form itself. Administrators stay locked to their own organization.
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Recent Activity"
            description="Review important access and provisioning changes made across SpaceM8."
            isDark={isDark}
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                    Audit trail
                  </p>
                  <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {activityLoading
                      ? "Loading latest events..."
                      : `${activityLogs.length} recent ${activityLogs.length === 1 ? "event" : "events"}`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={fetchActivityLogs}
                  className="text-xs font-semibold text-sky-700"
                >
                  {activityLoading ? "Refreshing..." : "Refresh"}
                </button>
              </div>

              {activityLogs.length === 0 ? (
                <div
                  className={`rounded-2xl border p-4 text-sm ${
                    isDark
                      ? "border-slate-800 bg-slate-950 text-slate-300"
                      : "border-slate-200 bg-slate-50 text-slate-600"
                  }`}
                >
                  No activity logs found yet.
                </div>
              ) : (
                activityLogs.map((log) => (
                  <div
                    key={log.id}
                    className={`rounded-2xl border p-4 ${
                      isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                          {log.action}
                        </p>
                        <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                          {log.entity_type} {log.entity_label ? `- ${log.entity_label}` : ""}
                        </p>
                      </div>
                      <span className="shrink-0 rounded-full bg-sky-100 px-2 py-1 text-[10px] font-semibold text-sky-700">
                        {new Date(log.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className={`mt-2 text-xs ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                      {log.user_email}
                    </p>
                  </div>
                ))
              )}
            </div>
          </SectionCard>

          {!userCanManageUsers ? (
            <SectionCard
              title="Access restricted"
              description="Only Growth and Administrator roles can create logins or change access levels."
              isDark={isDark}
            >
              <div
                className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${
                  isDark
                    ? "border-slate-800 bg-slate-950 text-slate-300"
                    : "border-slate-200 bg-slate-50 text-slate-600"
                }`}
              >
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                <p>
                  Your current role is {profile?.role || "Viewer"}. Ask a Growth or Administrator user to change user access.
                </p>
              </div>
            </SectionCard>
          ) : null}
        </div>
      </div>
    </AppLayout>
  );
}
